import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { trackError } from 'utils/helpers';
import LoadingSpinner from 'components/loading_spinner';
import { replaceBooking } from 'redux/actions/booking';
import { usePromoCodeMutation } from 'redux/api_slice/booking';
import { getCurrentBooking } from 'redux/selectors/current_booking';

const PromoCode = ({
  buttonClassName = 'btn btn-primary promo-code__apply',
}) => {
  const dispatch = useDispatch();
  const [promoCodeMutation, { error: promoCodeError }] = usePromoCodeMutation();
  const currentBooking = useSelector(getCurrentBooking);
  const existingPromoCode = currentBooking.promo_code;
  const [typedPromoCode, setTypedPromoCode] = useState(existingPromoCode || '');

  // Possible status: null, applying, applied, removing, failed
  const [promoCodeStatus, setPromoCodeStatus] = useState(
    existingPromoCode ? 'applied' : null
  );

  function handleInputChange(e) {
    const { value } = e.target;

    setPromoCodeStatus(null);
    setTypedPromoCode(value.trim());
  }

  async function applyPromoCode(code) {
    setPromoCodeStatus(code ? 'applying' : 'removing');

    try {
      const { error, data } = await promoCodeMutation({
        promo_code: code,
      });

      if (error) {
        throw error.data.message;
      }

      setPromoCodeStatus(code ? 'applied' : null);
      dispatch(replaceBooking(data));
    } catch (error) {
      setPromoCodeStatus('failed');
      trackError(error);
    }
  }

  return (
    <div className="promo-code space-y-1 w-100">
      <div className="input-group promo-code__group">
        <input
          type="text"
          className="form-control promo-code__input"
          value={typedPromoCode}
          disabled={['applying', 'applied', 'removing'].includes(
            promoCodeStatus
          )}
          onChange={handleInputChange}
        />
        {['applied', 'removing'].includes(promoCodeStatus) && (
          <span className="input-group-append d-flex align-item-center">
            <button
              className="btn p-0 ms-n4"
              type="button"
              onClick={async () => {
                await applyPromoCode('');
                setTypedPromoCode('');
              }}
              disabled={promoCodeStatus === 'removing'}
            >
              <i className="fal fa-times" />
            </button>
          </span>
        )}
        <button
          className={buttonClassName}
          type="button"
          onClick={() => applyPromoCode(typedPromoCode)}
          disabled={
            !typedPromoCode ||
            ['removing', 'applying', 'applied'].includes(promoCodeStatus)
          }
        >
          Apply
        </button>
      </div>

      {promoCodeStatus === 'removing' && (
        <LoadingSpinner text="Removing promo code..." />
      )}
      {promoCodeStatus === 'applying' && (
        <LoadingSpinner text={`Applying promo code: ${typedPromoCode}`} />
      )}
      {promoCodeStatus === 'applied' && (
        <div className="promo-code__text promo-code__text-saved">
          <p className="text-success">
            <i className="fas fa-check-circle text-success" /> {typedPromoCode}{' '}
            promo code applied
          </p>
        </div>
      )}
      {promoCodeStatus === 'failed' && (
        <div className="promo-code__text text-danger">
          {promoCodeError.data.message}
        </div>
      )}
    </div>
  );
};

PromoCode.propTypes = {
  buttonClassName: PropTypes.string,
};

export default PromoCode;
